import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Plus, Type, Sparkles, Square, Shapes, Snowflake } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { useComposition } from "../contexts/CompositionContext";

// Liste plate des 8 configurateurs avec leur catégorie & icône.
// Ordre = même ordre que dans le mega-menu Header pour cohérence.
export const CONFIGURATOR_TABS = [
  { slug: "croix-classique-led", cat: "croix-pharmacie", icon: Plus,
    label: { fr: "Croix classique", de: "Klassisches Kreuz", en: "Classic cross", it: "Croce classica" } },
  { slug: "croix-point-a-point", cat: "croix-pharmacie", icon: Plus,
    label: { fr: "Croix point à point", de: "Punkt-zu-Punkt-Kreuz", en: "Dot-matrix cross", it: "Croce punto a punto" } },
  { slug: "croix-neon-led", cat: "croix-pharmacie", icon: Plus,
    label: { fr: "Croix néon LED", de: "Neon-LED-Kreuz", en: "LED neon cross", it: "Croce neon LED" } },
  { slug: "lettre-boitier-alu", cat: "enseigne-pharmacie", icon: Type,
    label: { fr: "Lettres boîtier", de: "Gehäusebuchstaben", en: "Box letters", it: "Lettere scatolate" } },
  { slug: "lettre-retro-eclaire", cat: "enseigne-pharmacie", icon: Type,
    label: { fr: "Lettres rétro", de: "Rückseitig beleuchtet", en: "Backlit letters", it: "Lettere retro" } },
  { slug: "lettre-inox-3d", cat: "enseigne-pharmacie", icon: Square,
    label: { fr: "Lettres inox 3D", de: "Edelstahl 3D", en: "Inox 3D letters", it: "Lettere inox 3D" } },
  { slug: "enseigne-boitier-chassis", cat: "enseigne-pharmacie", icon: Shapes,
    label: { fr: "Enseigne châssis", de: "Rahmenschild", en: "Frame sign", it: "Insegna telaio" } },
  { slug: "enseigne-interieur-neon-led", cat: "enseigne-pharmacie", icon: Sparkles,
    label: { fr: "Néon intérieur", de: "Innen-Neon", en: "Indoor neon", it: "Neon interno" } },
  { slug: "caducee", cat: "enseigne-pharmacie", icon: Snowflake,
    label: { fr: "Caducée", de: "Caduceus", en: "Caduceus", it: "Caduceo" } },
];

export default function ConfiguratorTabs({ currentSlug }) {
  const { lang } = useI18n();
  const { summary } = useComposition();
  const { setPreviousSlug } = useComposition();
  const location = useLocation();
  // Lookup rapide slug → nombre d'éléments depuis le summary.
  const countBySlug = Object.fromEntries(summary.slugs.map((s) => [s.slug, s.count]));

  // 3 rangées : Croix / Lettres + Châssis / Néon intérieur + Caducée
  const ROWS = [
    {
      key: "croix",
      label: { fr: "Croix", de: "Kreuze", en: "Crosses", it: "Croci" },
      slugs: ["croix-classique-led", "croix-point-a-point", "croix-neon-led"],
    },
    {
      key: "lettres",
      label: { fr: "Lettres & enseigne châssis", de: "Buchstaben & Rahmen", en: "Letters & frame", it: "Lettere & telaio" },
      slugs: ["lettre-boitier-alu", "lettre-retro-eclaire", "lettre-inox-3d", "enseigne-boitier-chassis"],
    },
    {
      key: "neon",
      label: { fr: "Néon & Caducée", de: "Neon & Caduceus", en: "Neon & Caduceus", it: "Neon & Caduceo" },
      slugs: ["enseigne-interieur-neon-led", "caducee"],
    },
  ];

  const renderTab = (tab) => {
    const Icon = tab.icon;
    const active = currentSlug === tab.slug;
    const count = countBySlug[tab.slug] || 0;
    const label = tab.label[lang] || tab.label.fr;
    return (
      <Link
        key={tab.slug}
        to={`/produits/${tab.cat}/${tab.slug}`}
        data-testid={`config-tab-${tab.slug}`}
        onClick={() => {
          if (currentSlug && currentSlug !== tab.slug) setPreviousSlug(currentSlug);
        }}
        className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 border font-mono text-[10px] uppercase tracking-widest transition-colors whitespace-nowrap ${
          active
            ? "border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14]"
            : "border-white/10 bg-transparent text-zinc-400 hover:border-white/30 hover:text-zinc-200"
        }`}
        aria-current={active ? "page" : undefined}
      >
        <Icon size={12} />
        <span>{label}</span>
        {count > 0 && (
          <span
            data-testid={`config-tab-count-${tab.slug}`}
            className={`ml-1 px-1.5 py-0.5 text-[9px] ${active ? "bg-[#39FF14]/20 text-[#39FF14]" : "bg-white/10 text-zinc-300"}`}
          >
            {count}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div
      data-testid="configurator-tabs"
      className="-mx-1 mb-5 space-y-2 pb-3 border-b border-white/5"
    >
      {ROWS.map((row) => {
        const tabs = row.slugs
          .map((s) => CONFIGURATOR_TABS.find((t) => t.slug === s))
          .filter(Boolean);
        if (tabs.length === 0) return null;
        return (
          <div key={row.key} data-testid={`configurator-row-${row.key}`} className="flex items-center gap-3">
            <span className="shrink-0 hidden md:inline-block w-44 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">
              {row.label[lang] || row.label.fr}
            </span>
            <div className="flex items-center gap-1 flex-wrap">
              {tabs.map(renderTab)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
